import { useEffect, useState, type FormEvent } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { CLASSE_CHAMP, CLASSE_LIBELLE, CLASSE_ZONE_TEXTE } from '../veille/format';
import {
  useDeclarerApplicabilite,
  useDeposerPreuve,
  useRattacherPreuve,
  useRetirerPreuve,
  type IndicateurQualiopi,
  type PreuveQualiopi,
  type Rattachement,
  type ReponseQualiopi,
} from '../../lib/useQualiopi';

/**
 * Gestes de l'espace de conformité Qualiopi (TF-431) : déposer une preuve,
 * changer ses rattachements, la retirer, déclarer un indicateur non
 * applicable. Les refus viennent de l'API ; l'écran ne rejoue aucune règle,
 * sauf ce qui empêche d'envoyer un formulaire incomplet.
 */

type Referentiel = ReponseQualiopi['referentiel'];

function messageErreur(erreur: unknown): string | null {
  if (!erreur) return null;
  return erreur instanceof ErreurApi ? erreur.message : 'L’opération n’a pas abouti.';
}

function aujourdHuiIso(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Indian/Reunion' });
}

function PiedFormulaire({
  enCours,
  libelle,
  onAnnuler,
}: {
  enCours: boolean;
  libelle: string;
  onAnnuler: () => void;
}): JSX.Element {
  return (
    <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
      <BoutonSecondaire onClick={onAnnuler}>Annuler</BoutonSecondaire>
      <BoutonPrincipal type="submit" disabled={enCours}>
        {enCours ? 'Enregistrement…' : libelle}
      </BoutonPrincipal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rattachements
// ---------------------------------------------------------------------------

/** Une ligne par indicateur : la preuve qu'elle remplit, ou « preuve complémentaire ». */
export function EditeurRattachements({
  referentiel,
  rattachements,
  onChange,
}: {
  referentiel: Referentiel;
  rattachements: Rattachement[];
  onChange: (rattachements: Rattachement[]) => void;
}): JSX.Element {
  const pris = new Set(rattachements.map((lien) => lien.indicateur));
  const libre = referentiel.find((indicateur) => !pris.has(indicateur.numero));

  const changer = (rang: number, suivant: Rattachement): void =>
    onChange(rattachements.map((lien, index) => (index === rang ? suivant : lien)));

  return (
    <fieldset className="space-y-2">
      <legend className={CLASSE_LIBELLE}>Indicateurs auxquels la preuve répond</legend>
      {rattachements.map((lien, rang) => {
        const indicateur = referentiel.find((candidat) => candidat.numero === lien.indicateur);
        return (
          <div key={rang} className="grid grid-cols-1 gap-2 rounded-xl border border-gray-100 p-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
            <label className="block min-w-0">
              <span className="sr-only">Indicateur</span>
              <select
                value={lien.indicateur || ''}
                onChange={(evenement) =>
                  changer(rang, { indicateur: Number(evenement.target.value), attendu: null })
                }
                className={CLASSE_CHAMP}
              >
                <option value="">Choisir un indicateur…</option>
                {referentiel.map((option) => (
                  <option
                    key={option.numero}
                    value={option.numero}
                    disabled={option.numero !== lien.indicateur && pris.has(option.numero)}
                  >
                    {option.numero} · {option.titre}
                  </option>
                ))}
              </select>
            </label>
            <label className="block min-w-0">
              <span className="sr-only">Preuve attendue</span>
              <select
                value={lien.attendu ?? ''}
                onChange={(evenement) => changer(rang, { ...lien, attendu: evenement.target.value || null })}
                disabled={!indicateur}
                className={CLASSE_CHAMP}
              >
                <option value="">Preuve complémentaire</option>
                {indicateur?.attendus.map((attendu) => (
                  <option key={attendu.code} value={attendu.code}>
                    {attendu.libelle}
                  </option>
                ))}
              </select>
            </label>
            {rattachements.length > 1 ? (
              <button
                type="button"
                onClick={() => onChange(rattachements.filter((_, index) => index !== rang))}
                aria-label={`Retirer le rattachement ${rang + 1}`}
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl text-codialis-gray transition hover:bg-gray-50 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            ) : (
              <span aria-hidden className="hidden sm:block sm:w-11" />
            )}
          </div>
        );
      })}
      {libre && (
        <button
          type="button"
          onClick={() => onChange([...rattachements, { indicateur: 0, attendu: null }])}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-2 text-xs font-semibold text-codialis-sky hover:underline"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Rattacher aussi à un autre indicateur
        </button>
      )}
    </fieldset>
  );
}

function rattachementsComplets(rattachements: Rattachement[]): string | null {
  if (rattachements.length === 0) return 'Rattachez la preuve à au moins un indicateur.';
  if (rattachements.some((lien) => !lien.indicateur)) return 'Choisissez l’indicateur de chaque rattachement.';
  return null;
}

// ---------------------------------------------------------------------------
// Dépôt
// ---------------------------------------------------------------------------

export function ModaleDepotPreuve({
  ouverte,
  onFermer,
  referentiel,
  formats,
  sites,
  rattachementInitial,
}: {
  ouverte: boolean;
  onFermer: () => void;
  referentiel: Referentiel;
  formats: ReponseQualiopi['formats'];
  /** Antennes que l'utilisateur peut viser. */
  sites: { code: string; nom: string }[];
  rattachementInitial: Rattachement | null;
}): JSX.Element {
  const notifier = useToast();
  const deposer = useDeposerPreuve();
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [support, setSupport] = useState<'FICHIER' | 'LIEN'>('FICHIER');
  const [fichier, setFichier] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const [dateDocument, setDateDocument] = useState(aujourdHuiIso());
  const [valableJusquAu, setValableJusquAu] = useState('');
  const [site, setSite] = useState('');
  const [rattachements, setRattachements] = useState<Rattachement[]>([]);
  const [refus, setRefus] = useState<string | null>(null);

  useEffect(() => {
    if (!ouverte) return;
    setTitre('');
    setDescription('');
    setSupport('FICHIER');
    setFichier(null);
    setUrl('');
    setDateDocument(aujourdHuiIso());
    setValableJusquAu('');
    setSite('');
    setRattachements([rattachementInitial ?? { indicateur: 0, attendu: null }]);
    setRefus(null);
    deposer.reset();
    // Réinitialisée à chaque ouverture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouverte]);

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    const manque =
      rattachementsComplets(rattachements) ??
      (support === 'FICHIER' && !fichier ? 'Choisissez le fichier de la preuve.' : null) ??
      (support === 'LIEN' && !url.trim() ? 'Donnez l’adresse de la page qui porte la preuve.' : null);
    setRefus(manque);
    if (manque) return;
    deposer.mutate(
      {
        titre,
        description,
        fichier: support === 'FICHIER' ? fichier : null,
        url: support === 'LIEN' ? url.trim() : '',
        dateDocument,
        valableJusquAu,
        site,
        rattachements,
      },
      {
        onSuccess: () => {
          notifier('Preuve déposée');
          onFermer();
        },
      },
    );
  };

  const tailleMax = Math.round(formats.tailleMaxOctets / 1024 / 1024);

  return (
    <Modale
      ouverte={ouverte}
      onFermer={onFermer}
      titre="Déposer une preuve"
      sousTitre="Un fichier ou l’adresse d’une page, rattaché aux indicateurs auxquels il répond"
      largeur="max-w-2xl"
    >
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6">
        <label className="block min-w-0">
          <span className={CLASSE_LIBELLE}>Titre</span>
          <input
            value={titre}
            onChange={(evenement) => setTitre(evenement.target.value)}
            maxLength={200}
            required
            placeholder="Livret d’accueil 2026, procédure réclamations…"
            className={CLASSE_CHAMP}
          />
        </label>

        <fieldset className="min-w-0">
          <legend className={CLASSE_LIBELLE}>Support</legend>
          <div className="grid grid-cols-2 gap-2">
            {(['FICHIER', 'LIEN'] as const).map((choix) => (
              <button
                key={choix}
                type="button"
                aria-pressed={support === choix}
                onClick={() => setSupport(choix)}
                className={`min-h-[44px] rounded-xl border px-3 text-sm font-semibold transition ${
                  support === choix
                    ? 'border-codialis-navy bg-codialis-navy text-white'
                    : 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
                }`}
              >
                {choix === 'FICHIER' ? 'Fichier' : 'Lien vers une page'}
              </button>
            ))}
          </div>
        </fieldset>

        {support === 'FICHIER' ? (
          <label className="block min-w-0">
            <span className={CLASSE_LIBELLE}>Fichier</span>
            <input
              type="file"
              accept={formats.extensions.join(',')}
              onChange={(evenement) => setFichier(evenement.target.files?.[0] ?? null)}
              className={`${CLASSE_CHAMP} py-2`}
            />
            <span className="mt-1 block text-[11px] text-codialis-gray">
              {formats.libelle} · {tailleMax} Mo au plus
            </span>
          </label>
        ) : (
          <label className="block min-w-0">
            <span className={CLASSE_LIBELLE}>Adresse de la page</span>
            <input
              type="url"
              inputMode="url"
              value={url}
              onChange={(evenement) => setUrl(evenement.target.value)}
              placeholder="https://"
              className={CLASSE_CHAMP}
            />
          </label>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block min-w-0">
            <span className={CLASSE_LIBELLE}>Date du document</span>
            <input
              type="date"
              value={dateDocument}
              max={aujourdHuiIso()}
              onChange={(evenement) => setDateDocument(evenement.target.value)}
              required
              className={CLASSE_CHAMP}
            />
          </label>
          <label className="block min-w-0">
            <span className={CLASSE_LIBELLE}>Valable jusqu’au</span>
            <input
              type="date"
              value={valableJusquAu}
              min={dateDocument || undefined}
              onChange={(evenement) => setValableJusquAu(evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </label>
          <label className="block min-w-0">
            <span className={CLASSE_LIBELLE}>Portée</span>
            <select value={site} onChange={(evenement) => setSite(evenement.target.value)} className={CLASSE_CHAMP}>
              <option value="">Tout l’organisme</option>
              {sites.map((option) => (
                <option key={option.code} value={option.code}>
                  Antenne {option.code}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="-mt-2 text-[11px] text-codialis-gray">
          Sans fin de validité, la preuve vaut jusqu’à son retrait. Une preuve d’antenne ne couvre que cette antenne.
        </p>

        <EditeurRattachements referentiel={referentiel} rattachements={rattachements} onChange={setRattachements} />

        <label className="block min-w-0">
          <span className={CLASSE_LIBELLE}>Description (facultatif)</span>
          <textarea
            value={description}
            onChange={(evenement) => setDescription(evenement.target.value)}
            rows={3}
            maxLength={2000}
            className={CLASSE_ZONE_TEXTE}
          />
        </label>

        {(refus ?? messageErreur(deposer.error)) && <MessageErreur message={(refus ?? messageErreur(deposer.error))!} />}
        <PiedFormulaire enCours={deposer.isPending} libelle="Déposer" onAnnuler={onFermer} />
      </form>
    </Modale>
  );
}

// ---------------------------------------------------------------------------
// Rattachements d'une preuve déposée
// ---------------------------------------------------------------------------

export function ModaleRattachements({
  preuve,
  referentiel,
  onFermer,
}: {
  preuve: PreuveQualiopi;
  referentiel: Referentiel;
  onFermer: () => void;
}): JSX.Element {
  const notifier = useToast();
  const rattacher = useRattacherPreuve();
  const [rattachements, setRattachements] = useState<Rattachement[]>(
    preuve.rattachements.map((lien) => ({ indicateur: lien.indicateur, attendu: lien.attendu })),
  );
  const [refus, setRefus] = useState<string | null>(null);

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    const manque = rattachementsComplets(rattachements);
    setRefus(manque);
    if (manque) return;
    rattacher.mutate(
      { id: preuve.id, rattachements },
      {
        onSuccess: () => {
          notifier('Rattachements enregistrés');
          onFermer();
        },
      },
    );
  };

  return (
    <Modale ouverte onFermer={onFermer} titre="Rattachements" sousTitre={preuve.titre} largeur="max-w-2xl">
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6">
        <EditeurRattachements referentiel={referentiel} rattachements={rattachements} onChange={setRattachements} />
        {(refus ?? messageErreur(rattacher.error)) && (
          <MessageErreur message={(refus ?? messageErreur(rattacher.error))!} />
        )}
        <PiedFormulaire enCours={rattacher.isPending} libelle="Enregistrer" onAnnuler={onFermer} />
      </form>
    </Modale>
  );
}

// ---------------------------------------------------------------------------
// Retrait
// ---------------------------------------------------------------------------

export function ModaleRetraitPreuve({
  preuve,
  onFermer,
}: {
  preuve: PreuveQualiopi;
  onFermer: () => void;
}): JSX.Element {
  const notifier = useToast();
  const retirer = useRetirerPreuve();
  const [motif, setMotif] = useState('');

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    retirer.mutate(
      { id: preuve.id, motif },
      {
        onSuccess: () => {
          notifier('Preuve retirée');
          onFermer();
        },
      },
    );
  };

  return (
    <Modale ouverte onFermer={onFermer} titre="Retirer la preuve" sousTitre={preuve.titre} largeur="max-w-lg">
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6">
        <p className="text-sm text-codialis-gray">
          La preuve ne comptera plus pour aucun indicateur. Elle reste lisible, barrée, avec son motif.
        </p>
        <label className="block min-w-0">
          <span className={CLASSE_LIBELLE}>Motif</span>
          <textarea
            value={motif}
            onChange={(evenement) => setMotif(evenement.target.value)}
            rows={3}
            minLength={5}
            maxLength={1000}
            required
            placeholder="Remplacée par la version 2027, déposée par erreur…"
            className={CLASSE_ZONE_TEXTE}
          />
        </label>
        {messageErreur(retirer.error) && <MessageErreur message={messageErreur(retirer.error)!} />}
        <PiedFormulaire enCours={retirer.isPending} libelle="Retirer" onAnnuler={onFermer} />
      </form>
    </Modale>
  );
}

// ---------------------------------------------------------------------------
// Applicabilité
// ---------------------------------------------------------------------------

export function ModaleApplicabilite({
  indicateur,
  onFermer,
}: {
  indicateur: IndicateurQualiopi;
  onFermer: () => void;
}): JSX.Element {
  const notifier = useToast();
  const declarer = useDeclarerApplicabilite();
  const [motif, setMotif] = useState('');
  const revenir = indicateur.nonApplicable !== null;

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    declarer.mutate(
      { numero: indicateur.numero, nonApplicable: !revenir, ...(revenir ? {} : { motif }) },
      {
        onSuccess: () => {
          notifier(revenir ? `Indicateur ${indicateur.numero} de nouveau applicable` : `Indicateur ${indicateur.numero} déclaré non applicable`);
          onFermer();
        },
      },
    );
  };

  return (
    <Modale
      ouverte
      onFermer={onFermer}
      titre={revenir ? 'Rendre l’indicateur applicable' : 'Déclarer l’indicateur non applicable'}
      sousTitre={`Indicateur ${indicateur.numero} · ${indicateur.titre}`}
      largeur="max-w-lg"
    >
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6">
        <p className="text-sm text-codialis-gray">
          Portée : {indicateur.portee.libelle}.{' '}
          {revenir
            ? 'Ses exigences compteront de nouveau dans la conformité.'
            : 'L’auditeur demandera pourquoi l’organisme n’est pas concerné : le motif lui est destiné.'}
        </p>
        {!revenir && (
          <label className="block min-w-0">
            <span className={CLASSE_LIBELLE}>Motif</span>
            <textarea
              value={motif}
              onChange={(evenement) => setMotif(evenement.target.value)}
              rows={3}
              minLength={5}
              maxLength={1000}
              required
              placeholder="Aucune prestation sous-traitée ni portage salarial…"
              className={CLASSE_ZONE_TEXTE}
            />
          </label>
        )}
        {messageErreur(declarer.error) && <MessageErreur message={messageErreur(declarer.error)!} />}
        <PiedFormulaire
          enCours={declarer.isPending}
          libelle={revenir ? 'Rendre applicable' : 'Déclarer non applicable'}
          onAnnuler={onFermer}
        />
      </form>
    </Modale>
  );
}
